import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import UseToast from "../../UseToast";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { getLanguageText } from "../../../utils/multilanguageUtils";

const ButtonNavigations = ({
  btnId,
  userId,
  buttonColor,
  isFrontend,
  parentMenuID,
  linkbtnId,
  linkedTriviaa,
  mainMeuLinked,
  linkedWayfinder,
}) => {
  const [menus, setMenus] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const { showMsgSuccessToast, showErrorToast } = UseToast();

  const [selectedMenu, setSelectedMenu] = useState("");
  const [linkedTrivia, setLinkedTrivia] = useState("no_linkedTrivia");

  // New states for Wayfinder options - now three: directLocation, searchPage, and categories
  const [wayfinderOption, setWayfinderOption] = useState("directLocation");
  const [selectedDirectLocation, setSelectedDirectLocation] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);

  // State to track if wayfinder is enabled for this user
  const [isWayfinderEnabled, setIsWayfinderEnabled] = useState(false);

  // Current language for multilanguage support
  const [currentLanguage, setCurrentLanguage] = useState('en');

  const navigate = useNavigate();

  console.log("linkedWayfinder prop:", linkedWayfinder);

  // Load current language from localStorage and listen for changes
  useEffect(() => {
    if (userId) {
      const updateLanguage = () => {
        const savedLanguage = localStorage.getItem(`language_pref_${userId}`) || 'en';
        console.log("ButtonNavigations: Checking language from localStorage:", savedLanguage);
        if (savedLanguage !== currentLanguage) {
          console.log("ButtonNavigations: Language changed from", currentLanguage, "to", savedLanguage);
          setCurrentLanguage(savedLanguage);
        }
      };

      // Initial load
      updateLanguage();
      
      // Listen for storage changes (when language is changed in other components)
      const handleStorageChange = (e) => {
        if (e.key === `language_pref_${userId}`) {
          console.log("ButtonNavigations: Language changed via storage event:", e.newValue);
          setCurrentLanguage(e.newValue || 'en');
        }
      };
      
      window.addEventListener('storage', handleStorageChange);
      
      // Also check periodically in case storage event doesn't fire
      const interval = setInterval(updateLanguage, 2000); // Check every 2 seconds
      
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        clearInterval(interval);
      };
    }
  }, [userId]); // Removed currentLanguage from dependencies to avoid infinite loop

  // Debug log when current language changes
  useEffect(() => {
    console.log("ButtonNavigations: Current language changed to:", currentLanguage);
    console.log("ButtonNavigations: Available categories:", categories.map(cat => ({
      id: cat._id,
      name: cat.name,
      nameType: typeof cat.name
    })));
    console.log("ButtonNavigations: Available subcategories:", subCategories.map(sub => ({
      id: sub._id,
      name: sub.name,
      nameType: typeof sub.name
    })));
  }, [currentLanguage, categories, subCategories]);

  // Initialize all selections based on props - single useEffect to avoid conflicts
  useEffect(() => {
    console.log("Initializing selections from props:", {
      linkedTriviaa,
      linkedWayfinder,
      linkbtnId,
      mainMeuLinked,
    });

    // Determine the navigation type based on props
    if (linkedTriviaa) {
      console.log("Setting linkedTrivia to yes_linkedTrivia");
      setLinkedTrivia("yes_linkedTrivia");
      // Reset wayfinder states
      setWayfinderOption("directLocation");
      setSelectedDirectLocation("");
      setSelectedCategories([]);
      setSelectedSubCategories([]);
      setSelectedMenu("");
    } else if (linkedWayfinder?.status) {
      console.log(
        "Setting linkedTrivia to wayfinder with config:",
        linkedWayfinder,
      );
      setLinkedTrivia("wayfinder");
      setSelectedMenu("");

      // Set wayfinder options based on the configuration
      if (
        linkedWayfinder.directLocation?.status &&
        linkedWayfinder.directLocation.location
      ) {
        console.log(
          "Setting directLocation:",
          linkedWayfinder.directLocation.location,
        );
        setWayfinderOption("directLocation");
        // Handle both ObjectId objects and strings
        const locationId =
          typeof linkedWayfinder.directLocation.location === "object"
            ? linkedWayfinder.directLocation.location._id ||
              linkedWayfinder.directLocation.location.toString()
            : linkedWayfinder.directLocation.location;
        setSelectedDirectLocation(locationId);
        // Clear categories/subcategories for directLocation mode
        setSelectedCategories([]);
        setSelectedSubCategories([]);
      } else if (
        linkedWayfinder.linkedCategories?.status &&
        linkedWayfinder.linkedCategories.categories?.length > 0
      ) {
        console.log(
          "Setting categories:",
          linkedWayfinder.linkedCategories.categories,
        );
        setWayfinderOption("categories");
        setSelectedDirectLocation("");
        
        // Handle both ObjectId objects and strings in the array
        const categoryIds = linkedWayfinder.linkedCategories.categories.map(
          (cat) => (typeof cat === "object" ? cat._id || cat.toString() : cat),
        );
        console.log("Converted category IDs:", categoryIds);
        setSelectedCategories(categoryIds);
        
        // Load subcategories if present - IMPORTANT: Set these even if subcategories list isn't loaded yet
        if (
          linkedWayfinder.linkedSubCategories?.status &&
          linkedWayfinder.linkedSubCategories.subcategories?.length > 0
        ) {
          const subCategoryIds = linkedWayfinder.linkedSubCategories.subcategories.map(
            (sub) => (typeof sub === "object" ? sub._id || sub.toString() : sub),
          );
          console.log("Converted subcategory IDs:", subCategoryIds);
          setSelectedSubCategories(subCategoryIds);
        } else {
          // Explicitly clear if no subcategories in config
          setSelectedSubCategories([]);
        }
      } else if (linkedWayfinder.searchPage?.status) {
        console.log("Setting searchPage option");
        setWayfinderOption("searchPage");
        setSelectedDirectLocation("");
        setSelectedCategories([]);
        setSelectedSubCategories([]);
      }
    } else if (linkbtnId || mainMeuLinked) {
      console.log("Setting linkedTrivia to no_linkedTrivia");
      setLinkedTrivia("no_linkedTrivia");
      setSelectedMenu(mainMeuLinked ? "main_menu" : linkbtnId);
      // Reset wayfinder states
      setWayfinderOption("directLocation");
      setSelectedDirectLocation("");
      setSelectedCategories([]);
      setSelectedSubCategories([]);
    } else {
      console.log("Setting linkedTrivia to none");
      setLinkedTrivia("none");
      // Reset all states
      setSelectedMenu("");
      setWayfinderOption("directLocation");
      setSelectedDirectLocation("");
      setSelectedCategories([]);
      setSelectedSubCategories([]);
    }
  }, [linkedTriviaa, linkedWayfinder, linkbtnId, mainMeuLinked]);

  const handleRadioChange = (event) => {
    const value = event.target.value;
    setLinkedTrivia(value);

    if (value !== "no_linkedTrivia") {
      setSelectedMenu("");
    }
    if (value !== "wayfinder") {
      setSelectedDirectLocation("");
      setSelectedCategories([]);
      setWayfinderOption("directLocation");
    }
  };

  useEffect(() => {
    console.log("Checking if should fetch wayfinder data:", {
      linkedTrivia,
      userId,
    });
    if (linkedTrivia === "wayfinder" && userId) {
      console.log("Fetching locations and categories for wayfinder...");
      fetchLocations();
      fetchCategories();
    }
  }, [linkedTrivia, userId]);

  // Fetch subcategories when categories are selected OR when wayfinder option is categories
  useEffect(() => {
    if (wayfinderOption === "categories" && userId) {
      console.log("Fetching all subcategories for combined dropdown");
      fetchAllSubCategories();
    } else if (wayfinderOption !== "categories") {
      // Only clear subcategories if we're NOT in categories mode
      // Don't clear during initialization
      setSubCategories([]);
      // Don't clear selectedSubCategories here - let the initialization handle it
    }
  }, [wayfinderOption, userId]);

  // Debug log for selectedCategories changes
  useEffect(() => {
    console.log("selectedCategories changed:", selectedCategories);
    console.log(
      "categories available:",
      categories.map((c) => ({ id: c._id, name: c.name })),
    );
  }, [selectedCategories, categories]);

  // Debug log for selectedSubCategories changes
  useEffect(() => {
    console.log("selectedSubCategories changed:", selectedSubCategories);
    console.log(
      "subCategories available:",
      subCategories.map((s) => ({ id: s._id, name: s.name })),
    );
  }, [selectedSubCategories, subCategories]);

  // Debug log for categories and subcategories loading
  useEffect(() => {
    console.log("Categories or SubCategories updated:", {
      categoriesCount: categories.length,
      subCategoriesCount: subCategories.length,
      wayfinderOption,
      linkedTrivia
    });
    
    if (wayfinderOption === "categories") {
      console.log("Combined options available:", formatCombinedOptions.length);
    }
  }, [categories, subCategories, wayfinderOption]);

  // Fetch wayfinder enabled status for the user
  useEffect(() => {
    const fetchWayfinderStatus = async () => {
      if (!userId) return;

      try {
        const response = await axios.get(`/auth/settings/client/${userId}`);
        const wayfinderEnabled =
          response.data?.settings?.featureAccess?.wayfinderEnabled ?? false;
        setIsWayfinderEnabled(wayfinderEnabled);
        console.log("Wayfinder enabled status:", wayfinderEnabled);
      } catch (error) {
        console.error("Error fetching wayfinder status:", error);
        // Default to false if there's an error
        setIsWayfinderEnabled(false);
      }
    };

    fetchWayfinderStatus();
  }, [userId]);

  const fetchLocations = async () => {
    try {
      console.log("Fetching locations for userId:", userId);
      const response = await axios.get(
        `/api/wayfinder/get-locations/${userId}`,
      );
      console.log("Locations response:", response.data);
      if (response.data.success) {
        setLocations(response.data.data);
        console.log("Set locations:", response.data.data);
      } else {
        showErrorToast("Failed to fetch locations");
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      showErrorToast("Error loading locations");
    }
  };

  const fetchCategories = async () => {
    try {
      console.log("Fetching categories for userId:", userId);
      const response = await axios.get(
        `/api/wayfinder/get-categories/${userId}`,
      );
      console.log("Categories response:", response.data);
      if (response.data.success) {
        setCategories(response.data.data);
        console.log("Set categories:", response.data.data);
      } else {
        showErrorToast("Failed to fetch categories");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      showErrorToast("Error loading categories");
    }
  };

  const fetchSubCategoriesForCategories = async (categoryIds) => {
    try {
      console.log("Fetching subcategories for categories:", categoryIds);
      const response = await axios.post(
        `/api/wayfinder/get-subcategories-by-categories`,
        { categoryIds }
      );
      console.log("Subcategories response:", response.data);
      if (response.data.success) {
        setSubCategories(response.data.data);
        console.log("Set subcategories:", response.data.data);
      } else {
        showErrorToast("Failed to fetch subcategories");
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      showErrorToast("Error loading subcategories");
    }
  };

  const fetchAllSubCategories = async () => {
    try {
      console.log("Fetching all subcategories for userId:", userId);
      const response = await axios.get(
        `/api/wayfinder/subcategory/${userId}`,
      );
      console.log("All subcategories response:", response.data);
      if (response.data.success) {
        setSubCategories(response.data.data);
        console.log("Set all subcategories:", response.data.data);
      } else {
        showErrorToast("Failed to fetch subcategories");
      }
    } catch (error) {
      console.error("Error fetching all subcategories:", error);
      showErrorToast("Error loading subcategories");
    }
  };

  const handleDirectLocationChange = (event) => {
    setSelectedDirectLocation(event.target.value);
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleCombinedSelectionChange = (selected) => {
    if (!selected) {
      setSelectedCategories([]);
      setSelectedSubCategories([]);
      return;
    }

    const categoryIds = [];
    const subCategoryIds = [];

    selected.forEach((option) => {
      if (option.type === 'category') {
        categoryIds.push(option.id);
      } else if (option.type === 'subcategory') {
        subCategoryIds.push(option.id);
      }
    });

    setSelectedCategories(categoryIds);
    setSelectedSubCategories(subCategoryIds);
  };

  const handleCategorySelectionChange = (selected) => {
    const categoryIds = selected ? selected.map((option) => option.value) : [];
    setSelectedCategories(categoryIds);
  };

  const handleSubCategorySelectionChange = (selected) => {
    const subCategoryIds = selected ? selected.map((option) => option.value) : [];
    setSelectedSubCategories(subCategoryIds);
  };

  const formatCategoryOptions = (categories) => {
    return categories.map((cat) => ({
      value: cat._id,
      label: getLanguageText(cat.name, currentLanguage) || getLanguageText(cat.buttonText, currentLanguage) || 'Untitled Category',
    }));
  };

  const formatSubCategoryOptions = (subCategories) => {
    return subCategories.map((sub) => ({
      value: sub._id,
      label: getLanguageText(sub.name, currentLanguage) || getLanguageText(sub.buttonText, currentLanguage) || 'Untitled Subcategory',
    }));
  };

  // Combined format for categories and subcategories in one dropdown - memoized to update when language changes
  const formatCombinedOptions = useMemo(() => {
    const options = [];
    
    console.log("formatCombinedOptions useMemo executing:", {
      categoriesCount: categories.length,
      subCategoriesCount: subCategories.length,
      currentLanguage,
      sampleCategory: categories[0] ? {
        id: categories[0]._id,
        name: categories[0].name,
        nameType: typeof categories[0].name,
        extractedName: getLanguageText(categories[0].name, currentLanguage)
      } : null
    });
    
    // Add categories with a prefix
    if (categories && categories.length > 0) {
      categories.forEach((cat) => {
        if (cat && cat._id && cat.name) {
          const categoryName = getLanguageText(cat.name, currentLanguage) || getLanguageText(cat.buttonText, currentLanguage) || 'Untitled Category';
          console.log(`Processing category ${cat._id}:`, {
            originalName: cat.name,
            extractedName: categoryName,
            currentLanguage
          });
          options.push({
            value: `cat_${cat._id}`,
            label: `� ${categoryName}`,
            type: 'category',
            id: cat._id,
          });
        }
      });
      console.log(`Added ${categories.length} categories to options`);
    }
    
    // Add subcategories with a prefix
    if (subCategories && subCategories.length > 0) {
      subCategories.forEach((sub) => {
        if (sub && sub._id && sub.name) {
          const subcategoryName = getLanguageText(sub.name, currentLanguage) || getLanguageText(sub.buttonText, currentLanguage) || 'Untitled Subcategory';
          console.log(`Processing subcategory ${sub._id}:`, {
            originalName: sub.name,
            extractedName: subcategoryName,
            currentLanguage
          });
          options.push({
            value: `sub_${sub._id}`,
            label: `📂 ${subcategoryName}`,
            type: 'subcategory',
            id: sub._id,
          });
        }
      });
      console.log(`Added ${subCategories.length} subcategories to options`);
    }
    
    console.log("Final formatCombinedOptions result:", options);
    return options;
  }, [categories, subCategories, currentLanguage]);

  const getSelectedCategoryOptions = () => {
    if (
      !categories ||
      categories.length === 0 ||
      !selectedCategories ||
      selectedCategories.length === 0
    ) {
      return [];
    }

    console.log("Getting selected category options:", {
      categories: categories.map((c) => ({ id: c._id, name: c.name })),
      selectedCategories,
    });

    return categories
      .filter((cat) => {
        const catId = cat._id?.toString() || cat._id;
        return selectedCategories.some((selectedId) => {
          const selId =
            typeof selectedId === "object"
              ? selectedId._id?.toString() || selectedId.toString()
              : selectedId?.toString();
          return catId === selId;
        });
      })
      .map((cat) => ({
        value: cat._id?.toString() || cat._id,
        label: getLanguageText(cat.name, currentLanguage) || getLanguageText(cat.buttonText, currentLanguage) || 'Untitled Category',
      }));
  };

  const getSelectedSubCategoryOptions = () => {
    if (
      !subCategories ||
      subCategories.length === 0 ||
      !selectedSubCategories ||
      selectedSubCategories.length === 0
    ) {
      return [];
    }

    return subCategories
      .filter((sub) => {
        const subId = sub._id?.toString() || sub._id;
        return selectedSubCategories.some((selectedId) => {
          const selId =
            typeof selectedId === "object"
              ? selectedId._id?.toString() || selectedId.toString()
              : selectedId?.toString();
          return subId === selId;
        });
      })
      .map((sub) => ({
        value: sub._id?.toString() || sub._id,
        label: getLanguageText(sub.name, currentLanguage) || getLanguageText(sub.buttonText, currentLanguage) || 'Untitled Subcategory',
      }));
  };

  // Get selected combined options - memoized to update when language changes
  const getSelectedCombinedOptions = useMemo(() => {
    const selected = [];
    
    console.log("getSelectedCombinedOptions called:", {
      selectedCategories,
      selectedSubCategories,
      categoriesAvailable: categories.length,
      subCategoriesAvailable: subCategories.length,
      currentLanguage
    });
    
    // Add selected categories
    if (selectedCategories && selectedCategories.length > 0 && categories.length > 0) {
      selectedCategories.forEach((selectedId) => {
        // Normalize the selected ID
        const selId = typeof selectedId === "object"
          ? (selectedId._id?.toString() || selectedId.toString())
          : selectedId?.toString();
        
        // Find matching category
        const matchingCat = categories.find((cat) => {
          const catId = cat._id?.toString() || cat._id;
          return catId === selId;
        });
        
        if (matchingCat) {
          const categoryName = getLanguageText(matchingCat.name, currentLanguage) || getLanguageText(matchingCat.buttonText, currentLanguage) || 'Untitled Category';
          selected.push({
            value: `cat_${matchingCat._id}`,
            label: `📁 ${categoryName}`,
            type: 'category',
            id: matchingCat._id,
          });
          console.log("Added category to selection:", categoryName);
        } else {
          console.warn("Could not find category for ID:", selId);
        }
      });
    }
    
    // Add selected subcategories
    if (selectedSubCategories && selectedSubCategories.length > 0 && subCategories.length > 0) {
      selectedSubCategories.forEach((selectedId) => {
        // Normalize the selected ID
        const selId = typeof selectedId === "object"
          ? (selectedId._id?.toString() || selectedId.toString())
          : selectedId?.toString();
        
        // Find matching subcategory
        const matchingSub = subCategories.find((sub) => {
          const subId = sub._id?.toString() || sub._id;
          return subId === selId;
        });
        
        if (matchingSub) {
          const subcategoryName = getLanguageText(matchingSub.name, currentLanguage) || getLanguageText(matchingSub.buttonText, currentLanguage) || 'Untitled Subcategory';
          selected.push({
            value: `sub_${matchingSub._id}`,
            label: `📂 ${subcategoryName}`,
            type: 'subcategory',
            id: matchingSub._id,
          });
          console.log("Added subcategory to selection:", subcategoryName);
        } else {
          console.warn("Could not find subcategory for ID:", selId);
        }
      });
    }
    
    console.log("Final selected options:", selected);
    return selected;
  }, [selectedCategories, selectedSubCategories, categories, subCategories, currentLanguage]);

  useEffect(() => {
    // Get current language from localStorage
    const currentLanguage =
      localStorage.getItem(`language_pref_${userId}`) || "en";

    axios
      .get(`/api/primarymenus/${userId}/${currentLanguage}`)
      .then((response) => {
        if (response.data.error) {
          showErrorToast("No Menus Found");
        } else {
          console.log("Fetched menus response:", response);
          if (isFrontend) {
            setMenus(
              response.data?.data.menus.map(({ submenus, ...rest }) => rest),
            );
          } else {
            setMenus([
              ...response.data?.data.menus
                .map(({ submenus, ...rest }) => rest)
                .filter((e) => e._id !== parentMenuID),
              { _id: "main_menu", name: "Main Menu" },
            ]);
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching menus:", error);
      });
  }, [btnId, userId, isFrontend, parentMenuID]);

  const handleChange = (event) => {
    setSelectedMenu(event.target.value);
  };

  const handleSubmitPage = async (event) => {
    event.preventDefault();

    if (linkedTrivia === "no_linkedTrivia" && !selectedMenu) {
      showErrorToast("Please Select a Menu");
      return;
    }

    if (linkedTrivia === "wayfinder") {
      if (wayfinderOption === "directLocation" && !selectedDirectLocation) {
        showErrorToast("Please Select a Location");
        return;
      }
      if (wayfinderOption === "categories" && selectedCategories.length === 0 && selectedSubCategories.length === 0) {
        showErrorToast("Please Select at least one Category or Subcategory");
        return;
      }
      // searchPage doesn't require selection - it just opens the search page
    }

    try {
      let linkedWayfinderPayload = {
        status: linkedTrivia === "wayfinder",
        linkedCategories: {
          status: false,
          categories: [],
        },
        linkedSubCategories: {
          status: false,
          subcategories: [],
        },
        linkedLocations: {
          status: false,
          locations: [],
        },
        directLocation: {
          status: false,
          location: null,
        },
        searchPage: {
          status: false,
        },
      };

      if (linkedTrivia === "wayfinder") {
        if (wayfinderOption === "directLocation") {
          linkedWayfinderPayload.directLocation = {
            status: true,
            location: selectedDirectLocation,
          };
        } else if (wayfinderOption === "categories") {
          linkedWayfinderPayload.linkedCategories = {
            status: true,
            categories: selectedCategories,
          };
          // Add subcategories if selected
          if (selectedSubCategories.length > 0) {
            linkedWayfinderPayload.linkedSubCategories = {
              status: true,
              subcategories: selectedSubCategories,
            };
          }
        } else if (wayfinderOption === "searchPage") {
          linkedWayfinderPayload.searchPage = {
            status: true,
          };
        }
      }

      const payload = {
        userId: userId,
        btnId: btnId,
        isFrontend: isFrontend,
        parentMenuID: parentMenuID,
        linkedTrivia: linkedTrivia === "yes_linkedTrivia",
        menuId: linkedTrivia === "no_linkedTrivia" ? selectedMenu : null,
        linkedWayfinder: linkedWayfinderPayload,
      };

      console.log("Saving payload:", payload);
      const response = await axios.post(`/api/linkbutton`, payload);
      console.log("Save response:", response.data);
      showMsgSuccessToast("Button Successfully Linked");

      // Log the current state after successful save
      console.log("Successfully saved. Current state:", {
        linkedTrivia,
        wayfinderOption,
        selectedDirectLocation,
        selectedCategories,
        linkedWayfinderPayload,
      });
    } catch (error) {
      showErrorToast("Failed to link button");
      console.error("Error linking button:", error);
    }
  };

  // Inline styles
  const styles = {
    container: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "24px",
      fontFamily:
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    form: {
      width: "100%",
      maxWidth: "480px",
      backgroundColor: "#ffffff",
      borderRadius: "16px",
      boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
      padding: "28px",
    },
    sectionTitle: {
      fontSize: "13px",
      fontWeight: "600",
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      marginBottom: "16px",
    },
    radioGroup: {
      display: "flex",
      flexWrap: "wrap",
      gap: "10px",
      marginBottom: "24px",
    },
    radioCard: {
      flex: "1 1 calc(50% - 5px)",
      minWidth: "140px",
      padding: "14px 16px",
      borderRadius: "10px",
      border: "2px solid #e2e8f0",
      backgroundColor: "#f8fafc",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    radioCardActive: {
      borderColor: buttonColor || "#6366f1",
      backgroundColor: `${buttonColor || "#6366f1"}10`,
    },
    radioCircle: {
      width: "18px",
      height: "18px",
      borderRadius: "50%",
      border: "2px solid #cbd5e1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      transition: "all 0.2s ease",
    },
    radioCircleActive: {
      borderColor: buttonColor || "#6366f1",
      backgroundColor: buttonColor || "#6366f1",
    },
    radioInner: {
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      backgroundColor: "#ffffff",
    },
    radioLabel: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#334155",
      margin: 0,
    },
    radioLabelActive: {
      color: buttonColor || "#6366f1",
    },
    divider: {
      height: "1px",
      backgroundColor: "#e2e8f0",
      margin: "24px 0",
    },
    wayfinderOptions: {
      display: "flex",
      gap: "12px",
      marginBottom: "20px",
    },
    wayfinderCard: {
      flex: 1,
      padding: "20px 16px",
      borderRadius: "12px",
      border: "2px solid #e2e8f0",
      backgroundColor: "#ffffff",
      cursor: "pointer",
      transition: "all 0.2s ease",
      textAlign: "center",
    },
    wayfinderCardActive: {
      borderColor: buttonColor || "#6366f1",
      backgroundColor: `${buttonColor || "#6366f1"}08`,
      boxShadow: `0 4px 12px ${buttonColor || "#6366f1"}20`,
    },
    wayfinderIcon: {
      width: "48px",
      height: "48px",
      borderRadius: "12px",
      backgroundColor: "#f1f5f9",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 12px",
      transition: "all 0.2s ease",
    },
    wayfinderIconActive: {
      backgroundColor: buttonColor || "#6366f1",
    },
    wayfinderTitle: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#334155",
      margin: "0 0 4px 0",
    },
    wayfinderDesc: {
      fontSize: "12px",
      color: "#94a3b8",
      margin: 0,
    },
    selectWrapper: {
      marginTop: "16px",
    },
    selectLabel: {
      display: "block",
      fontSize: "13px",
      fontWeight: "500",
      color: "#475569",
      marginBottom: "8px",
    },
    select: {
      width: "100%",
      padding: "12px 16px",
      fontSize: "14px",
      border: "2px solid #e2e8f0",
      borderRadius: "10px",
      backgroundColor: "#ffffff",
      color: "#334155",
      cursor: "pointer",
      outline: "none",
      transition: "all 0.2s ease",
      appearance: "none",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 12px center",
      backgroundSize: "20px",
    },
    searchPageInfo: {
      marginTop: "16px",
      padding: "16px",
      backgroundColor: "#f0fdf4",
      borderRadius: "10px",
      border: "1px solid #bbf7d0",
    },
    searchPageText: {
      fontSize: "13px",
      color: "#166534",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    selectedCount: {
      fontSize: "12px",
      color: "#64748b",
      marginTop: "8px",
      fontWeight: "500",
    },
    submitButton: {
      width: "100%",
      padding: "14px 24px",
      fontSize: "15px",
      fontWeight: "600",
      color: "#ffffff",
      backgroundColor: buttonColor || "#6366f1",
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginTop: "24px",
      boxShadow: `0 4px 12px ${buttonColor || "#6366f1"}40`,
    },
  };

  return (
    <div style={styles.container}>
      <form onSubmit={handleSubmitPage} style={styles.form}>
        {/* Main Navigation Options */}
        <div style={styles.sectionTitle}>Select Navigation Type</div>
        <div style={styles.radioGroup}>
          {/* Menu Link */}
          <div
            style={{
              ...styles.radioCard,
              ...(linkedTrivia === "no_linkedTrivia"
                ? styles.radioCardActive
                : {}),
            }}
            onClick={() =>
              handleRadioChange({ target: { value: "no_linkedTrivia" } })
            }
          >
            <div
              style={{
                ...styles.radioCircle,
                ...(linkedTrivia === "no_linkedTrivia"
                  ? styles.radioCircleActive
                  : {}),
              }}
            >
              {linkedTrivia === "no_linkedTrivia" && (
                <div style={styles.radioInner} />
              )}
            </div>
            <label
              style={{
                ...styles.radioLabel,
                ...(linkedTrivia === "no_linkedTrivia"
                  ? styles.radioLabelActive
                  : {}),
              }}
            >
              Menu Link
            </label>
          </div>

          {/* Trivia Quiz */}
          <div
            style={{
              ...styles.radioCard,
              ...(linkedTrivia === "yes_linkedTrivia"
                ? styles.radioCardActive
                : {}),
            }}
            onClick={() =>
              handleRadioChange({ target: { value: "yes_linkedTrivia" } })
            }
          >
            <div
              style={{
                ...styles.radioCircle,
                ...(linkedTrivia === "yes_linkedTrivia"
                  ? styles.radioCircleActive
                  : {}),
              }}
            >
              {linkedTrivia === "yes_linkedTrivia" && (
                <div style={styles.radioInner} />
              )}
            </div>
            <label
              style={{
                ...styles.radioLabel,
                ...(linkedTrivia === "yes_linkedTrivia"
                  ? styles.radioLabelActive
                  : {}),
              }}
            >
              Trivia Quiz
            </label>
          </div>

          {/* Wayfinder - Only show if enabled */}
          {isWayfinderEnabled && (
            <div
              style={{
                ...styles.radioCard,
                ...(linkedTrivia === "wayfinder" ? styles.radioCardActive : {}),
              }}
              onClick={() =>
                handleRadioChange({ target: { value: "wayfinder" } })
              }
            >
              <div
                style={{
                  ...styles.radioCircle,
                  ...(linkedTrivia === "wayfinder"
                    ? styles.radioCircleActive
                    : {}),
                }}
              >
                {linkedTrivia === "wayfinder" && (
                  <div style={styles.radioInner} />
                )}
              </div>
              <label
                style={{
                  ...styles.radioLabel,
                  ...(linkedTrivia === "wayfinder"
                    ? styles.radioLabelActive
                    : {}),
                }}
              >
                Wayfinder
              </label>
            </div>
          )}

          {/* None */}
          <div
            style={{
              ...styles.radioCard,
              ...(linkedTrivia === "none" ? styles.radioCardActive : {}),
            }}
            onClick={() => handleRadioChange({ target: { value: "none" } })}
          >
            <div
              style={{
                ...styles.radioCircle,
                ...(linkedTrivia === "none" ? styles.radioCircleActive : {}),
              }}
            >
              {linkedTrivia === "none" && <div style={styles.radioInner} />}
            </div>
            <label
              style={{
                ...styles.radioLabel,
                ...(linkedTrivia === "none" ? styles.radioLabelActive : {}),
              }}
            >
              None
            </label>
          </div>
        </div>

        {/* Menu Link Dropdown */}
        {linkedTrivia === "no_linkedTrivia" && (
          <>
            <div style={styles.divider} />
            <div style={styles.selectWrapper}>
              <label style={styles.selectLabel}>Select Menu</label>
              <select
                value={selectedMenu}
                onChange={handleChange}
                style={styles.select}
              >
                <option value="">Choose a menu</option>
                {menus.map((menu) => (
                  <option key={menu._id} value={menu._id}>
                    {getLanguageText(menu.name, currentLanguage) || menu.name || 'Untitled Menu'}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Wayfinder Options - Only Direct Location and Search Page */}
        {linkedTrivia === "wayfinder" && (
          <>
            <div style={styles.divider} />
            <div style={styles.sectionTitle}>Wayfinder Option</div>
            <div style={styles.wayfinderOptions}>
              {/* Direct Location Card */}
              <div
                style={{
                  ...styles.wayfinderCard,
                  ...(wayfinderOption === "directLocation"
                    ? styles.wayfinderCardActive
                    : {}),
                }}
                onClick={() => setWayfinderOption("directLocation")}
              >
                <div
                  style={{
                    ...styles.wayfinderIcon,
                    ...(wayfinderOption === "directLocation"
                      ? styles.wayfinderIconActive
                      : {}),
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={
                      wayfinderOption === "directLocation"
                        ? "#ffffff"
                        : "#64748b"
                    }
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <p style={styles.wayfinderTitle}>Direct Location</p>
                <p style={styles.wayfinderDesc}>Go to specific location</p>
              </div>

              {/* Categories Card */}
              <div
                style={{
                  ...styles.wayfinderCard,
                  ...(wayfinderOption === "categories"
                    ? styles.wayfinderCardActive
                    : {}),
                }}
                onClick={() => setWayfinderOption("categories")}
              >
                <div
                  style={{
                    ...styles.wayfinderIcon,
                    ...(wayfinderOption === "categories"
                      ? styles.wayfinderIconActive
                      : {}),
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={
                      wayfinderOption === "categories" ? "#ffffff" : "#64748b"
                    }
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                </div>
                <p style={styles.wayfinderTitle}>Categories</p>
                <p style={styles.wayfinderDesc}>Show category list</p>
              </div>

              {/* Search Page Card */}
              <div
                style={{
                  ...styles.wayfinderCard,
                  ...(wayfinderOption === "searchPage"
                    ? styles.wayfinderCardActive
                    : {}),
                }}
                onClick={() => setWayfinderOption("searchPage")}
              >
                <div
                  style={{
                    ...styles.wayfinderIcon,
                    ...(wayfinderOption === "searchPage"
                      ? styles.wayfinderIconActive
                      : {}),
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={
                      wayfinderOption === "searchPage" ? "#ffffff" : "#64748b"
                    }
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                </div>
                <p style={styles.wayfinderTitle}>Search Page</p>
                <p style={styles.wayfinderDesc}>Open location search</p>
              </div>
            </div>

            {/* Direct Location Dropdown */}
            {wayfinderOption === "directLocation" && (
              <div style={styles.selectWrapper}>
                <label style={styles.selectLabel}>Select Location</label>
                <select
                  value={selectedDirectLocation}
                  onChange={handleDirectLocationChange}
                  style={styles.select}
                >
                  <option value="">Choose a location</option>
                  {locations.map((location) => (
                    <option key={location._id} value={location._id}>
                      {getLanguageText(location.name, currentLanguage) || getLanguageText(location.buttonText, currentLanguage) || 'Untitled Location'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Categories Multi-Select */}
            {wayfinderOption === "categories" && (
              <>
                <div style={styles.selectWrapper}>
                  <label style={styles.selectLabel}>Select Categories & Subcategories</label>
                  <Select
                    key={`select-${categories.length}-${subCategories.length}-${currentLanguage}`}
                    isMulti
                    options={formatCombinedOptions}
                    value={getSelectedCombinedOptions}
                    onChange={handleCombinedSelectionChange}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    placeholder="Select categories and subcategories..."
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        padding: "4px 8px",
                        fontSize: "14px",
                        border: `2px solid ${state.isFocused ? buttonColor || "#6366f1" : "#e2e8f0"}`,
                        borderRadius: "10px",
                        backgroundColor: "#ffffff",
                        boxShadow: state.isFocused
                          ? `0 0 0 3px ${buttonColor || "#6366f1"}20`
                          : "none",
                        "&:hover": {
                          borderColor: buttonColor || "#6366f1",
                        },
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: `${buttonColor || "#6366f1"}15`,
                        borderRadius: "6px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: buttonColor || "#6366f1",
                        fontWeight: "500",
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: buttonColor || "#6366f1",
                        "&:hover": {
                          backgroundColor: buttonColor || "#6366f1",
                          color: "#ffffff",
                        },
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? buttonColor || "#6366f1"
                          : state.isFocused
                            ? `${buttonColor || "#6366f1"}10`
                            : "#ffffff",
                        color: state.isSelected ? "#ffffff" : "#334155",
                        "&:active": {
                          backgroundColor: buttonColor || "#6366f1",
                        },
                      }),
                    }}
                  />
                  <small
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      marginTop: "8px",
                      display: "block",
                    }}
                  >
                    📁 = Category | 📂 = Subcategory - Select one or multiple items
                    {categories.length === 0 && subCategories.length === 0 && (
                      <span style={{ color: "#ef4444", fontWeight: 600 }}>
                        {" "}(Loading options...)
                      </span>
                    )}
                    <br />
                    <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                      Current Language: {currentLanguage} | 
                      <button 
                        type="button"
                        onClick={() => {
                          const newLang = localStorage.getItem(`language_pref_${userId}`) || 'en';
                          console.log("Manual refresh - localStorage language:", newLang);
                          setCurrentLanguage(newLang);
                        }}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#6366f1', 
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontSize: '11px',
                          marginLeft: '4px'
                        }}
                      >
                        Refresh Language
                      </button>
                    </span>
                  </small>
                  {(selectedCategories.length > 0 || selectedSubCategories.length > 0) && (
                    <div style={styles.selectedCount}>
                      {selectedCategories.length > 0 && `${selectedCategories.length} ${selectedCategories.length === 1 ? "category" : "categories"}`}
                      {selectedCategories.length > 0 && selectedSubCategories.length > 0 && " + "}
                      {selectedSubCategories.length > 0 && `${selectedSubCategories.length} ${selectedSubCategories.length === 1 ? "subcategory" : "subcategories"}`}
                      {" selected"}
                    </div>
                  )}
                </div>

                {/* Order Categories & Subcategories Button */}
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "12px",
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#334155",
                        }}
                      >
                        Order Categories & Subcategories
                      </p>
                      <p
                        style={{
                          margin: "4px 0 0 0",
                          fontSize: "12px",
                          color: "#64748b",
                        }}
                      >
                        Set the display order for selected categories and subcategories
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        // Just pass buttonId - the backend will fetch the button's category/subcategory configuration
                        const params = new URLSearchParams();
                        if (btnId) {
                          params.append('buttonId', btnId);
                        }
                        const queryString = params.toString();
                        navigate(`/category-order/${userId}${queryString ? `?${queryString}` : ''}`);
                      }}
                      disabled={selectedCategories.length === 0 && selectedSubCategories.length === 0}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: (selectedCategories.length === 0 && selectedSubCategories.length === 0) 
                          ? "#cbd5e1" 
                          : buttonColor || "#6366f1",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: 600,
                        cursor: (selectedCategories.length === 0 && selectedSubCategories.length === 0) 
                          ? "not-allowed" 
                          : "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        transition: "all 0.2s ease",
                        opacity: (selectedCategories.length === 0 && selectedSubCategories.length === 0) 
                          ? 0.6 
                          : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (selectedCategories.length > 0 || selectedSubCategories.length > 0) {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = `0 4px 12px ${buttonColor || "#6366f1"}40`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="8" y1="6" x2="21" y2="6" />
                        <line x1="8" y1="12" x2="21" y2="12" />
                        <line x1="8" y1="18" x2="21" y2="18" />
                        <line x1="3" y1="6" x2="3.01" y2="6" />
                        <line x1="3" y1="12" x2="3.01" y2="12" />
                        <line x1="3" y1="18" x2="3.01" y2="18" />
                      </svg>
                      Order Items
                    </button>
                  </div>
                  {(selectedCategories.length === 0 && selectedSubCategories.length === 0) && (
                    <p style={{
                      margin: "8px 0 0 0",
                      fontSize: "12px",
                      color: "#ef4444",
                      fontStyle: "italic",
                    }}>
                      Please select at least one category or subcategory to enable ordering
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Search Page Info */}
            {wayfinderOption === "searchPage" && (
              <>
                <div style={styles.searchPageInfo}>
                  <p style={styles.searchPageText}>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#166534"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    This will open the Wayfinder search page where users can
                    search for locations.
                  </p>
                </div>

                {/* Order Locations Button */}
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "12px",
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#334155",
                        }}
                      >
                        Order Locations
                      </p>
                      <p
                        style={{
                          margin: "4px 0 0 0",
                          fontSize: "12px",
                          color: "#64748b",
                        }}
                      >
                        Set the display order for locations in search page
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        // Add buttonId for button-specific ordering
                        const params = new URLSearchParams();
                        if (btnId) {
                          params.append('buttonId', btnId);
                        }
                        const queryString = params.toString();
                        navigate(`/location-order/${userId}${queryString ? `?${queryString}` : ''}`);
                      }}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: buttonColor || "#6366f1",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = `0 4px 12px ${buttonColor || "#6366f1"}40`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="8" y1="6" x2="21" y2="6" />
                        <line x1="8" y1="12" x2="21" y2="12" />
                        <line x1="8" y1="18" x2="21" y2="18" />
                        <line x1="3" y1="6" x2="3.01" y2="6" />
                        <line x1="3" y1="12" x2="3.01" y2="12" />
                        <line x1="3" y1="18" x2="3.01" y2="18" />
                      </svg>
                      Order Locations
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          style={styles.submitButton}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = `0 6px 20px ${buttonColor || "#6366f1"}50`;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = `0 4px 12px ${buttonColor || "#6366f1"}40`;
          }}
        >
          Save Navigation
        </button>
      </form>
    </div>
  );
};

export default ButtonNavigations;
